/**
 * Funções utilitárias para formatação de dados
 */

/**
 * Formata um CPF (000.000.000-00)
 * @param {string} cpf - CPF a ser formatado
 * @returns {string} CPF formatado
 */
export const formatarCPF = (cpf) => {
  if (!cpf) return '';
  const numeros = cpf.replace(/\D/g, '');
  if (numeros.length <= 11) {
    return numeros.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }
  return numeros;
};

/**
 * Formata um CNPJ (00.000.000/0000-00)
 * @param {string} cnpj - CNPJ a ser formatado
 * @returns {string} CNPJ formatado
 */
export const formatarCNPJ = (cnpj) => {
  if (!cnpj) return '';
  const numeros = cnpj.replace(/\D/g, '');
  if (numeros.length <= 11) return numeros; // CPF (não mexe aqui)
  return numeros.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
};

/**
 * Formata um número de telefone ((00) 00000-0000)
 * @param {string} telefone - Telefone a ser formatado
 * @returns {string} Telefone formatado
 */
export const formatarTelefone = (telefone) => {
  if (!telefone) return '';
  const numeros = telefone.replace(/\D/g, '');
  if (numeros.length <= 10) {
    return numeros.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  }
  return numeros.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
};

/**
 * Formata data e hora (DD/MM/AAAA HH:MM)
 * @param {Date} dataHora - Data e hora a ser formatada
 * @returns {string} Data e hora formatada
 */
export const formatarDataHora = (dataHora) => {
  if (!dataHora) return '';

  const data = typeof dataHora === 'string' ? new Date(dataHora) : dataHora;

  if (data instanceof Date) {
    if (isNaN(data.getTime())) return '';
    const dia = String(data.getDate()).padStart(2, '0');
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const ano = data.getFullYear();
    const horas = String(data.getHours()).padStart(2, '0');
    const minutos = String(data.getMinutes()).padStart(2, '0');
    return `${dia}/${mes}/${ano} ${horas}:${minutos}`;
  }

  return '';
};

/**
 * Formata um valor monetário (R$ 0.000,00)
 * @param {number|string} valor - Valor a ser formatado
 * @returns {string} Valor formatado
 */
export const formatarMoeda = (valor) => {
  if (!valor && valor !== 0) return '';
  const numero = typeof valor === 'string' ? parseFloat(valor) : valor;
  if (isNaN(numero)) return '';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(numero);
};

/**
 * Formata um CEP (00.000-000)
 * @param {string} cep - CEP a ser formatado
 * @returns {string} CEP formatado
 */
export const formatarCEP = (cep) => {
  if (!cep) return '';
  const numeros = cep.replace(/\D/g, '');
  if (numeros.length > 5) {
    return numeros.replace(/^(\d{5})(\d{0,3})/, '$1-$2');
  }
  return numeros;
};

/**
 * Remove formatação, mantendo apenas números
 * @param {string} valor - Valor a ser limpo
 * @returns {string} Valor contendo apenas números
 */
export const limparFormatacao = (valor) => {
  if (!valor) return '';
  return valor.replace(/\D/g, '');
};

/**
 * Formata uma data para input (DD/MM/AAAA)
 * @param {string} valor - Valor da data
 * @returns {string} Data formatada
 */
export const formatarData = (valor) => {
  if (!valor) return '';
  const numeros = valor.replace(/\D/g, '');

  if (numeros.length <= 2) return numeros;
  if (numeros.length <= 4) return numeros.replace(/(\d{2})(\d+)/, '$1/$2');
  return numeros.replace(/(\d{2})(\d{2})(\d{0,4})/, '$1/$2/$3');
};

/**
 * Converte data ISO (AAAA-MM-DD) para BR (DD/MM/AAAA)
 */
export const converterDataParaBR = (dataISO) => {
  if (!dataISO) return '';
  try {
    const dataLimpa = dataISO.split('T')[0];
    const [ano, mes, dia] = dataLimpa.split('-');
    if (!ano || !mes || !dia) return dataISO;
    return `${dia}/${mes}/${ano}`;
  } catch (e) {
    return dataISO;
  }
};

/**
 * Converte data BR (DD/MM/AAAA) para ISO (AAAA-MM-DD)
 */
export const converterDataParaISO = (dataBR) => {
  if (!dataBR) return null;
  const numeros = dataBR.replace(/\D/g, '');
  if (numeros.length !== 8) return null;

  const dia = numeros.substr(0, 2);
  const mes = numeros.substr(2, 2);
  const ano = numeros.substr(4, 4);

  return `${ano}-${mes}-${dia}`;
};
